
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import { isEqual } from 'lodash'

import { City } from '@/model/City'
import { Snapshot } from '@/model/Snapshot'
import { TimeStep } from '@/model/TimeStep'
import { IMapService } from '@/services/map/IMapService'
import { ISettingService } from '@/services/setting/ISettingService'

import { MapBoxGL } from './MapBoxGL'
import { IController } from './controllers/IController'
import { IControllerParent } from './controllers/IControllerParent'
import { CityController } from './controllers/CityController'
import { RainfallController } from './controllers/RainfallController'
import { ReturnPeriodController } from './controllers/ReturnPeriodController'
import { FloodRiskController } from './controllers/FloodRiskController'
import { FloodDepthController } from './controllers/FloodDepthController'
import { StationController } from './controllers/StationController'
import { RainGaugeController } from './controllers/RainGaugeController'
import { getEnv } from '@/utils/env'
import translateOrFallback from '@/utils/localization'
import { TranslateResult } from 'vue-i18n'
import { RegularRepresentationTypes, Representation, RepresentationTypes } from '@/model/Representation'
import { BaseController } from './controllers/BaseController'
import { CumulativeRainfall } from '@/model/CumulativeRainFall'
import { CumulativeRainfallController } from './controllers/CumulativeRainfallController'

const APPLICATION_PREFIX = 'flood4cast'

type MapLoadingState = 'loading' | 'loaded' | 'empty'

@Component({
  components: {},
})
export default class OverviewMap extends Vue implements IControllerParent {
  @Ref() readonly mapElement!: HTMLElement

  @Prop() private representation!: Representation<RepresentationTypes>
  @Prop() private city?: City
  @Prop() private snapshot?: Snapshot<TimeStep>
  @Prop() private timeStep?: TimeStep
  @Prop({ type: Boolean, default: false }) private maxMode?: boolean

  private mapService!: IMapService
  private settingService!: ISettingService

  private mapBoxGL!: MapBoxGL
  private cityController!: CityController
  private regularRepresentationControllers!: Record<RegularRepresentationTypes, IController>
  private cumulativeRainfallRepresentationControllers: Map<CumulativeRainfall, IController> = new Map()
  private stationController!: StationController
  private rainGaugeController!: RainGaugeController

  private layerOpacity = 80

  private cityIsDirty = true

  private loadState: MapLoadingState = 'loading'

  private scale = 500

  private intervalId: number | null = null

  created(): void {
    this.mapService = this.$services.get<IMapService>('map')
    this.settingService = this.$services.get<ISettingService>('setting')
  }

  async mounted(): Promise<void> {
    if (!this.$refs.mapElement) {
      throw Error('The map component cannot be created.')
    }

    let center: [number, number] = [0, 0]
    if (this.city) {
      const coordinates = this.city.getCoordinates()
      center = [coordinates.getLongitude(), coordinates.getLatitude()]
    }

    const bounds = getEnv('VUE_APP_MAP_BOUNDS') as number[]

    this.mapBoxGL = new MapBoxGL(
      APPLICATION_PREFIX,
      this.$refs.mapElement as HTMLElement,
      getEnv('VUE_APP_MAPBOX_ACCES_TOKEN') as string,
      center,
      bounds,
      getEnv('VUE_APP_MAPBOX_STYLE_URL') as string,
      this.onMapLoaded,
    )

    this.regularRepresentationControllers = {
      rainfall: new RainfallController(this, this.mapBoxGL, this.mapService),
      'return period': new ReturnPeriodController(this, this.mapBoxGL, this.mapService),
      'flood risk': new FloodRiskController(this, this.mapBoxGL, this.mapService),
      'flood depth': new FloodDepthController(this, this.mapBoxGL, this.mapService),
    }
    this.cityController = new CityController(this, this.mapBoxGL, this.mapService)

    this.stationController = new StationController(this, this.mapBoxGL, this.mapService, this.$services)
    this.rainGaugeController = new RainGaugeController(this, this.mapBoxGL, this.mapService, this.$services)
  }

  beforeDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
    this.stationController.beforeDestroy()
    this.rainGaugeController.beforeDestroy()
  }

  async onMapLoaded(): Promise<void> {
    Object.values(this.regularRepresentationControllers).forEach((rc) => rc.initialize())
    this.cityController.initialize()
    this.stationController.initialize()
    this.rainGaugeController.initialize()

    this.mapBoxGL.addEventHandler('zoom', (/*event: MapboxEvent*/) => {
      const currentZoom = this.mapBoxGL.getZoom()

      let newScale = this.scale
      if (this.scale != 500 && currentZoom < 12) {
        newScale = 500
      } else if (this.scale != 100 && currentZoom >= 12 && currentZoom < 14) {
        newScale = 100
      } else if (this.scale != 20 && currentZoom >= 14) {
        newScale = 20
      }

      if (this.representation.type === 'flood depth' && this.scale !== newScale) {
        this.scale = newScale
        this.load()
      } else {
        this.scale = newScale
      }
    })

    this.load()
  }

  async load(): Promise<void> {
    if (!this.timeStep) {
      console.log('something went wrong')
    }
    if (!this.mapBoxGL.isReady() || !this.city || !this.representation) {
      return
    }

    // const controller = this.regularRepresentationControllers[this.representation.type]
    // Todo add cumulative rainfall controller
    const controller = (() => {
      if (this.representation.type === 'cumulative rainfall') {
        const controllerExist = this.cumulativeRainfallRepresentationControllers.has(
          this.representation.cumulativeRainfall,
        )
        if (controllerExist)
          return this.cumulativeRainfallRepresentationControllers.get(this.representation.cumulativeRainfall)
        const controller = new CumulativeRainfallController(this, this.mapBoxGL, this.mapService, this.representation)
        controller.initialize()
        this.cumulativeRainfallRepresentationControllers.set(this.representation.cumulativeRainfall, controller)
        return controller
      }

      return this.regularRepresentationControllers[this.representation.type]
    })()

    if (!controller) {
      this.mapBoxGL.showOnlyLayers([])
      return
    }
    this.loadState = 'loading'

    if (this.cityIsDirty) {
      const coordinates = this.city.getCoordinates()
      this.mapBoxGL.jumpTo([coordinates.getLongitude(), coordinates.getLatitude()])
      await this.cityController.load(this.city, this.timeStep, true)
    }

    let mapHasData = false
    if (this.timeStep) {
      mapHasData = await controller.load(
        this.city,
        this.timeStep,
        this.cityIsDirty && this.city.getContours() === null,
        this.scale,
      )

      await this.stationController.load(this.city, this.timeStep, true)
      this.$emit('has-stations', this.stationController.hasStation())
    }

    if (this.snapshot) {
      await this.rainGaugeController.load(this.city, this.snapshot.getReference(), true)
    }

    if (!this.timeStep) {
      this.loadState = 'loading'
    } else if (!mapHasData) {
      this.loadState = 'empty'
    } else {
      this.loadState = 'loaded'
    }
    this.cityIsDirty = false

    if (controller instanceof BaseController) {
      for (const layer of (controller as BaseController).layers) {
        this.mapBoxGL.setOpacityForLayer(layer.id, this.layerOpacity)
      }
    }
  }

  getCurrentTimeStep(): TimeStep | null {
    return this.timeStep ? this.timeStep : null
  }

  isMaxMode(): boolean {
    return this.maxMode !== undefined ? this.maxMode : false
  }

  async hasRainGauges(): Promise<boolean> {
    const settings = await this.settingService.getSettings()
    return settings.getHasRainGauges()
  }

  translateOrFallback(key: string, fallback: string): string | TranslateResult {
    return translateOrFallback(key, fallback)
  }

  formatOpacity(val: number): string {
    return `${val}%`
  }

  @Watch('city')
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  onCityChanged(city: City | null): void {
    this.cityIsDirty = true
    this.load()
  }

  @Watch('representation')
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  async onRepresentationChanged(representation: Representation<RepresentationTypes>): Promise<void> {
    this.stationController.clearCities()
    this.rainGaugeController.clearRainGauges()
    console.log('rep')
    console.log(this.timeStep?.timeStampAsString())
    this.load()
  }

  @Watch('timeStep')
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  async onTimeStepChanged(timeStep: TimeStep | null, previousTimeStep: TimeStep | null): Promise<void> {
    if (!timeStep) {
      return
    }
    await this.load()
    // if (previousTimeStep === null || this.representation === FloodRisk || timeStep !== previousTimeStep) {
    //     await this.load()
    //     return
    // }
  }

  @Watch('snapshot')
  async onSnapshotChanged(
    snapshot: Snapshot<TimeStep> | null,
    previousSnapshot: Snapshot<TimeStep> | null,
  ): Promise<void> {
    if (!snapshot) {
      return
    }

    if (!(await this.hasRainGauges())) {
      return
    }

    const newReference = snapshot.getReference()
    const previousReference = previousSnapshot?.getReference()

    if (isEqual(newReference, previousReference)) {
      return
    }

    await this.rainGaugeController.reloadRainGauges(newReference)
  }

  @Watch('maxMode')
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  onMaxModeChanged(maxMode: boolean): void {
    this.load()
  }

  @Watch('layerOpacity')
  onLayerOpacityChanged(opacity: number): void {
    if (!this.representation) {
      return
    }

    const controller =
      this.representation.type === 'cumulative rainfall'
        ? this.cumulativeRainfallRepresentationControllers.get(this.representation.cumulativeRainfall)
        : this.regularRepresentationControllers[this.representation.type]

    if (controller instanceof BaseController) {
      for (const layer of (controller as BaseController).layers) {
        this.mapBoxGL.setOpacityForLayer(layer.id, opacity)
      }
    }
  }
}
